import { createGlobalStyle, ThemeContext } from 'styled-components'
import React, { useContext, JSX } from 'react'
import {
  GlobalStyle,
  pxToRems,
} from '@awaze/design-system.components.global-style'
import { AgnosticTheme } from '@awaze/design-system.components.awaze-theme-provider'

/* visit
-> /node_modules/@awazevr/design-dictionary/build/web/cottages/index.js
- for the full list of tokens and their values.
- */

const GlobalStyleVars = createGlobalStyle`
  html {
  /* Color */
  --main-bg-color: ${({ theme }) => theme.ColorThemeBackgroundPage};
  --main-text-color: ${({ theme }) => theme.ColorGrey800};
  --main-text-alternate-color: ${({ theme }) => theme.ColorWhiteOpacity100};
  --main-white-color: ${({ theme }) => theme.ColorWhiteOpacity100};

  /* Alt Colors */
  --color-black-opacity-05: ${({ theme }) => theme.ColorBlackOpacity5};
  --color-black-opacity-10: ${({ theme }) => theme.ColorBlackOpacity10};

  /* Theme colors */
  --theme-cta-primary: ${({ theme }) => theme.ColorThemeCtaPrimary};
  --theme-cta-primary: ${({ theme }) => theme.ColorThemeCtaPrimary};
  --theme-accent-1: ${({ theme }) => theme.ColorThemeAccent1};
  --theme-core-green: ${({ theme }) => theme.ColorBrandCottagesComCoreGreen};
  --theme-link: ${({ theme }) => theme.ColorThemeLink};
  --theme-link-active: ${({ theme }) => theme.ColorThemeLinkActive};
  --theme-light-100: ${({ theme }) => theme.ColorThemeLight100};
  --theme-secondary-400: ${({ theme }) => theme.ColorThemeSecondary400};
  --theme-secondary-500: ${({ theme }) => theme.ColorThemeSecondary500};
  --theme-secondary-600: ${({ theme }) => theme.ColorThemeSecondary600};
  --theme-secondary-700: ${({ theme }) => theme.ColorThemeSecondary700};
  --status-attention-300: ${({ theme }) => theme.ColorStatusAttention300};

  /* Breakpoints */
  --breakpoint--medium: 768px;
  --breakpoint--large: 1200px;

  /* Typography */
  --ff-Heading-100: ${({ theme }) => theme.FontHeadingHeading100FontFamily};
  --ff-Text-100: ${({ theme }) => theme.FontTextText100FontFamily};

  /* Headings */
  /* fs = font-size, lh = line-height, fw = font-weight */

  --fs-Heading--600: ${({ theme }) =>
    pxToRems(theme.FontHeadingHeading600FontSize)};
  --lh-Heading--600: ${({ theme }) =>
    pxToRems(theme.FontHeadingHeading600LineHeight)};
  --fw-Heading--600: ${({ theme }) =>
    pxToRems(theme.FontHeadingHeading600FontWeight)};

  --fs-Heading--500: ${({ theme }) =>
    pxToRems(theme.FontHeadingHeading500FontSize)};
  --lh-Heading--500: ${({ theme }) =>
    pxToRems(theme.FontHeadingHeading500LineHeight)};
  --fw-Heading--500: ${({ theme }) =>
    pxToRems(theme.FontHeadingHeading500FontWeight)};

  --fs-Heading--400: ${({ theme }) =>
    pxToRems(theme.FontHeadingHeading400FontSize)};
  --lh-Heading--400: ${({ theme }) =>
    pxToRems(theme.FontHeadingHeading400LineHeight)};
  --fw-Heading--400: ${({ theme }) =>
    pxToRems(theme.FontHeadingHeading400FontWeight)};

  --fs-Heading--300: ${({ theme }) =>
    pxToRems(theme.FontHeadingHeading300FontSize)};
  --lh-Heading--300: ${({ theme }) =>
    pxToRems(theme.FontHeadingHeading300LineHeight)};
  --fw-Heading--300: ${({ theme }) =>
    pxToRems(theme.FontHeadingHeading300FontWeight)};

  --fs-Heading--200: ${({ theme }) =>
    pxToRems(theme.FontHeadingHeading200FontSize)};
  --lh-Heading--200: ${({ theme }) =>
    pxToRems(theme.FontHeadingHeading200LineHeight)};
  --fw-Heading--200: ${({ theme }) =>
    pxToRems(theme.FontHeadingHeading200FontWeight)};

  --fs-Heading--100: ${({ theme }) =>
    pxToRems(theme.FontHeadingHeading100FontSize)};
  --lh-Heading--100: ${({ theme }) =>
    pxToRems(theme.FontHeadingHeading100LineHeight)};
  --fw-Heading--100: ${({ theme }) =>
    pxToRems(theme.FontHeadingHeading100FontWeight)};



  /* Body Texts */
  --fs-Text--500: ${({ theme }) => pxToRems(theme.FontTextText500FontSize)};
  --lh-Text--500: ${({ theme }) => pxToRems(theme.FontTextText500LineHeight)};
  --fw-Text--500: ${({ theme }) => pxToRems(theme.FontTextText500FontWeight)};
  --fw-Text--500-mid: ${({ theme }) => theme.FontTextText500MidFontWeight};

  --fs-Text--400: ${({ theme }) => pxToRems(theme.FontTextText400FontSize)};
  --lh-Text--400: ${({ theme }) => pxToRems(theme.FontTextText400LineHeight)};
  --fw-Text--400: ${({ theme }) => theme.FontTextText400FontWeight};

  --fs-Text--300: ${({ theme }) => pxToRems(theme.FontTextText300FontSize)};
  --lh-Text--300: ${({ theme }) => pxToRems(theme.FontTextText300LineHeight)};
  --fw-Text--300: ${({ theme }) => theme.FontTextText300FontWeight};

  --fs-Text--200: ${({ theme }) => pxToRems(theme.FontTextText200FontSize)};
  --lh-Text--200: ${({ theme }) => pxToRems(theme.FontTextText200LineHeight)};
  --fw-Text--200: ${({ theme }) => theme.FontTextText200FontWeight};

  --fs-Text--100: ${({ theme }) => pxToRems(theme.FontTextText100FontSize)};
  --lh-Text--100: ${({ theme }) => pxToRems(theme.FontTextText100LineHeight)};
  --fw-Text--100: ${({ theme }) => theme.FontTextText100FontWeight};

  /* Text-shadows */
  --text-shadow-3: 0px 0.4px 0.8px rgba(0, 0, 0, 0.1),
    0px 3px 6px rgba(0, 0, 0, 0.2);
  --text-shadow-1: 0px 0.4px 0.8px rgba(0, 0, 0, 0.1),
    0px 3px 6px rgba(0, 0, 0, 0.2);
  }
`

export const GlobalStyling = (): JSX.Element => {
  const themeContext = useContext(ThemeContext) || AgnosticTheme

  return (
    <>
      <GlobalStyleVars theme={themeContext} />
      <GlobalStyle />
    </>
  )
}
